import { useState, useEffect, useCallback, useRef } from "react";
import { fetchProducts } from "@/lib/api";

const MAX_RETRIES = 4;
const RETRY_DELAY_MS = 4000; // fits a Render free-tier cold start (~30-60s total across retries)

/**
 * Fetches live, active products/categories for a given section from the
 * backend so Admin Portal edits reflect on the website immediately.
 *
 * Automatically retries a few times on failure (e.g. the backend waking up
 * from a free-tier cold start) instead of leaving the section empty and
 * requiring the visitor to hard-refresh. Previously-loaded items are never
 * cleared just because a background refetch failed.
 */
export function useProducts(category) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const retryCountRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedOnceRef.current) setLoading(true);
    setError(null);
    try {
      const data = await fetchProducts(category);
      setItems(data);
      hasLoadedOnceRef.current = true;
      retryCountRef.current = 0;
      setIsReconnecting(false);
    } catch (e) {
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        setIsReconnecting(true);
        setTimeout(load, RETRY_DELAY_MS);
        return; // don't clear existing items or flip loading off yet — we're retrying
      }
      setError(e);
      setIsReconnecting(false);
    } finally {
      if (hasLoadedOnceRef.current || retryCountRef.current === 0) {
        setLoading(false);
      }
    }
  }, [category]);

  useEffect(() => {
    hasLoadedOnceRef.current = false;
    retryCountRef.current = 0;
    load();
  }, [load]);

  return { items, loading, error, isReconnecting, reload: load };
}
